import { intlLocale, msg, t } from "./i18n";

// Formate in der Sprache des Nutzers – erst beim ersten Aufruf angelegt (Sprache steht vor dem ersten Rendern fest)
let cache: { nf: Intl.NumberFormat; money: Intl.NumberFormat; rtf: Intl.RelativeTimeFormat; region: Intl.DisplayNames } | null = null;
const f = () => (cache ??= {
  nf: new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: 3 }),
  money: new Intl.NumberFormat(intlLocale(), { style: "currency", currency: "EUR" }),
  rtf: new Intl.RelativeTimeFormat(intlLocale(), { numeric: "auto" }),
  region: new Intl.DisplayNames([intlLocale()], { type: "region" }),
});

export const fmtNumber = (n: number | undefined | null) => (n == null ? "—" : f().nf.format(n));
export const fmtMoney = (minor: number, currency = "EUR") =>
  currency === "EUR" ? f().money.format(minor / 100) : new Intl.NumberFormat(intlLocale(), { style: "currency", currency }).format(minor / 100);

export function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  const abs = Math.abs(diff);
  if (abs < 60) return t("gerade eben");
  if (abs < 3600) return f().rtf.format(Math.round(diff / 60), "minute");
  if (abs < 86_400) return f().rtf.format(Math.round(diff / 3600), "hour");
  if (abs < 30 * 86_400) return f().rtf.format(Math.round(diff / 86_400), "day");
  return new Date(iso).toLocaleDateString(intlLocale());
}

export const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString(intlLocale(), { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

/** Sendungsverfolgung je Carrier (Fallback: DHL) */
export const trackingUrl = (carrier: string, n: string) =>
  /dpd/i.test(carrier) ? `https://tracking.dpd.de/status/de_DE/parcel/${n}`
  : /gls/i.test(carrier) ? `https://gls-group.com/DE/de/paketverfolgung?match=${n}`
  : /ups/i.test(carrier) ? `https://www.ups.com/track?tracknum=${n}`
  : /hermes/i.test(carrier) ? `https://www.myhermes.de/empfangen/sendungsverfolgung/sendungsinformation#${n}`
  : `https://www.dhl.de/de/privatkunden/pakete-empfangen/verfolgen.html?piececode=${n}`;

/** Ländername in der Sprache des Nutzers ("DE" → "Deutschland" / "Germany"). Keine Flaggen-Emojis: Windows stellt sie nur als Buchstaben dar. */
export const countryName = (cc: string) => {
  try {
    return f().region.of(cc.toUpperCase()) ?? cc;
  } catch {
    return cc;
  }
};

/** Kurze, handlungsorientierte Labels je Prüfregel – für Filter-Chips und "Zu erledigen" */
export const RULE_LABELS: Record<string, string> = {
  "sku.required": msg("SKU fehlt"),
  "sku.format": msg("SKU mit unsichtbaren Zeichen"),
  "sku.max_length": msg("SKU zu lang"),
  "sku.unique": msg("Doppelte SKU"),
  "name.required": msg("Bezeichnung fehlt"),
  "name.max_length": msg("Bezeichnung zu lang"),
  "gtin.recommended": msg("EAN fehlt"),
  "gtin.checksum": msg("EAN ungültig"),
  "gtin.restricted_prefix": msg("Interne EAN"),
  "gtin.unique": msg("Doppelte EAN"),
  "weight.required": msg("Gewicht fehlt"),
  "weight.plausible": msg("Gewicht unplausibel"),
  "weight.density": msg("Gewicht passt nicht zur Größe"),
  "dimensions.required": msg("Maße fehlen"),
  "dimensions.plausible": msg("Maße unplausibel"),
  "customs.hs_code": msg("Zolltarifnummer fehlt"),
  "customs.origin_country": msg("Ursprungsland fehlt"),
  "customs.value": msg("Warenwert fehlt"),
  "handling.dangerous_goods": msg("Gefahrgut unvollständig"),
  "price.non_negative": msg("Negativer Preis"),
  "bundle.components": msg("Stückliste fehlerhaft"),
};

export const ruleLabel = (id: string) => (RULE_LABELS[id] ? t(RULE_LABELS[id]) : id);
